import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import PageHeader from "../../../core/common/commonComponents/PageHeader";
import StatusFilterHeader from "../../../core/common/commonComponents/StatusFilterHeader";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel } from "../../../core/common/GlobalFunctions/exportTableData";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import TdsTaxRegimeModal from "./modal/taxRegimeModal";
import { deleteTdsTaxRegime, fetchTdsTaxRegimes } from "../../../core/data/redux/payroll/TDS/tdsTaxRegime/tdsTaxRegimeActions";
import { decryptData } from "../../../core/utils/encryptUtils";

const TdsTaxRegimeList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { regimes = [] } = useSelector((state: any) => state.TDS.tdsTaxRegime);
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  // Permission
  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const showModal = (type: 'edit' | 'create') => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setModalType('');
    setIsModalVisible(false);
  };

  useEffect(() => {
    if (!regimes.length) {
      dispatch(fetchTdsTaxRegimes());
    }
  }, [dispatch, regimes.length]);

  const filteredData = useMemo(() => {
    const filters = selectedStatus !== "All" ? { Status: selectedStatus } : {};
    return filterData(regimes, filters, "CreatedAt", "CreatedAt", selectedSort);
  }, [regimes, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, selectedStatus, selectedSort, regimes]);

  const exportData = filteredData.map((item: any) => ({
    RegimeName: item.RegimeName || "N/A",
    "Financial Year": item.FinancialYearName || "N/A",
    IsDefault: item.IsDefault ? "Yes" : "No",
    AllowsStandardDeduction: item.AllowsStandardDeduction ? "Yes" : "No",
    StandardDeductionAmount: item.StandardDeductionAmount || 0,
    AllowsChapterVI_ADeductions: item.AllowsChapterVI_ADeductions ? "Yes" : "No",
    Rebate87A_MaxIncome: item.RebateUnderSection87A_MaxIncome || 0,
    Rebate87A_Amount: item.RebateUnderSection87A_Amount || 0,
    Status: item.Status || "Inactive",
  }));

  const handleDeleteRegime = async (id: number) => {
    try {
      const result = await dispatch(deleteTdsTaxRegime(id));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('Tax Regime deleted successfully');
        setIsDeleteModalVisible(false);
        await dispatch(fetchTdsTaxRegimes());
      } else {
        message.error((result.payload as { error: string })?.error || 'Delete failed');
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const columns = [
    {
      title: "Regime Name",
      dataIndex: "RegimeName",
      sorter: (a: any, b: any) => a.RegimeName.localeCompare(b.RegimeName),
      render: (_: any, record: any) => (
        <div
          className="d-flex align-items-center"
          style={{ gap: 8 }}
          onMouseEnter={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "1")}
          onMouseLeave={(e) => e.currentTarget.querySelector("button")?.style.setProperty("opacity", "0")}
        >
          <span>{record.RegimeName}</span>
          {canEdit && (
            <button
              className="btn btn-link p-0"
              onClick={() => {
                showModal("edit");
                setSelectedRowData(record);
              }}
              style={{
                opacity: 0, 
                cursor: "pointer",
                transition: "opacity 0.3s",
              }}
              title="Edit"
            >
              <i className="ti ti-pencil" />
            </button>
          )}
        </div>
      ),
    },
    {
      title: "Financial Year",
      dataIndex: "FinancialYearName",
    },
    {
      title: "Default",
      dataIndex: "IsDefault",
      render: (val: number) => (
        <span className={`badge ${val ? 'badge-success' : 'badge-secondary'}`}>
          {val ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Standard Deduction",
      dataIndex: "AllowsStandardDeduction",
      render: (val: number) => (
        <span className={`badge ${val ? 'badge-success' : 'badge-secondary'}`}>
          {val ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Std Deduction Amt",
      dataIndex: "StandardDeductionAmount",
    },
    {
      title: "Chapter VI-A",
      dataIndex: "AllowsChapterVI_ADeductions",
      render: (val: number) => (
        <span className={`badge ${val ? 'badge-success' : 'badge-secondary'}`}>
          {val ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Rebate Max Income",
      dataIndex: "RebateUnderSection87A_MaxIncome",
    },
    {
      title: "Rebate Amount",
      dataIndex: "RebateUnderSection87A_Amount",
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (status: string) => (
        <span className={`badge ${status === 'active' ? 'badge-success' : 'badge-danger'}`}>
          <i className="ti ti-point-filled me-1" />
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: "Action",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          {canEdit && (
            <button
              className="me-2"
              onClick={() => {
                showModal("edit");
                setSelectedRowData(record);
              }}
              title="Edit"
            >
              <i className="ti ti-edit" />
            </button>
          )}
          {canDelete && (
            <button
              disabled={record.Status === "inactive"}
              onClick={() => {
                setSelectedRowData(record);
                setIsDeleteModalVisible(true);
              }}
              title="Delete"
            >
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <PageHeader
            title="Tax Regimes"
            breadcrumbs={[
              { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
              { label: "TDS" },
              { label: "Tax Regimes", isActive: true },
            ]}
            listViewPath="#"
            onExportCSV={canExport ? () => exportToCSV(exportData, "TdsTaxRegimes") : undefined}
            onExportExcel={canExport ? () => exportToExcel(exportData, "TdsTaxRegimes") : undefined}
            onAdd={(canAdd ? () => showModal("create") : undefined)}
            addLabel="Add Tax Regime"
            exportEnabled={canExport}
            addButtonEnabled={canAdd}
            rightExtra={<CollapseHeader />}
          />

          <div className="card">
            <StatusFilterHeader
              title="Tax Regime List"
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      <TdsTaxRegimeModal
        modalType={modalType}
        handleCancel={handleCancel}
        isModalVisible={isModalVisible}
        selectedRowData={selectedRowData}
      />

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.TaxRegimeID && handleDeleteRegime(selectedRowData?.TaxRegimeID)
        }
      />
    </>
  );
};

export default TdsTaxRegimeList;
